import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '@/lib/apiClient'
import { queryKeys } from '@/lib/query/keys'

interface PaymentConfig {
  teamBillingEnabled?: boolean
}

/**
 * Probe `GET /payment/config` for release-gated billing flags. Team billing is
 * server-gated by `ENABLE_TEAM_BILLING`; default the flag **false** (Team
 * hidden) until the probe confirms it's on, and keep it false on error (#4836).
 *
 * The probe failure is swallowed to `null` inside the queryFn so a config
 * outage never surfaces as a query error to callers (Team simply stays hidden)
 * — and so test rejections never escape as unhandled promise rejections.
 *
 * `isResolved` distinguishes a *definitive* probe answer (success) from the
 * loading window and from a swallowed error (both leave `data` non-object).
 * Callers that must not over-hide Team on a transient outage — e.g. the
 * always-shown upgrade/first_login picker — gate on `isResolved && !enabled`.
 */
export function usePaymentConfig(authToken: string | null): {
  teamBillingEnabled: boolean
  isLoading: boolean
  isResolved: boolean
} {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.payment.config(),
    queryFn: async (): Promise<PaymentConfig | null> => {
      try {
        return await apiFetch<PaymentConfig>('payment/config', {
          authToken: authToken ?? undefined,
        })
      } catch {
        return null
      }
    },
    enabled: Boolean(authToken),
    staleTime: 5 * 60_000,
  })
  return {
    teamBillingEnabled: Boolean(data?.teamBillingEnabled),
    isLoading,
    // data is the probe object only on success; undefined while loading and
    // null when the probe errored — both mean "no definitive answer yet".
    isResolved: data != null,
  }
}
